import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageSeo from '@/components/base/PageSeo';
import { comics, categories } from '@/mocks/comics';

function ComicCard({ comic }: { comic: typeof comics[0] }) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/comic/${comic.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              comic.status === 'ongoing'
                ? 'bg-primary-500 text-background-50'
                : 'bg-secondary-500 text-background-50'
            }`}
          >
            {comic.status === 'ongoing' ? t('home.statusOngoing') : t('home.statusCompleted')}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="flex items-center gap-2 text-background-50 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
              {comic.rating}
            </span>
            <span className="opacity-60">
              {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}{t('home.views')}
            </span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1">
          {comic.author} · {t('home.updatedAt')} {comic.updatedAt}
        </p>
      </div>
    </Link>
  );
}

function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20beautiful%20artistic%20Korean%20webtoon%20style%20illustration%20featuring%20a%20collection%20of%20vibrant%20comic%20panels%20floating%20in%20a%20dreamy%20pastel%20atmosphere%20with%20soft%20gradients%20of%20warm%20coral%20pink%20and%20cream%2C%20subtle%20cherry%20blossom%20petals%20drifting%2C%20ethereal%20lighting%2C%20whimsical%20and%20inviting%20mood%2C%20clean%20composition%20with%20ample%20negative%20space%20for%20text%20overlay&width=1600&height=900&seq=hero-bg-hanman&orientation=landscape"
        alt="韩漫网 - 韩漫漫画大全"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      <div className="relative z-10 text-center px-4 w-full max-w-[800px] mx-auto">
        <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
          {t('home.heroTitle')}
        </h1>
        <p className="text-base md:text-lg text-background-200 mb-8 max-w-[600px] mx-auto">
          {t('home.heroSubtitle')}
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
          >
            {t('home.heroCta')}
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
          >
            {t('home.heroBrowse')}
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoryPills() {
  return (
    <section className="max-w-[1280px] mx-auto px-4 md:px-6 -mt-8 relative z-20">
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 flex flex-wrap items-center justify-center gap-2">
        <Link
          to="/category/all"
          className="px-4 py-2 rounded-full text-sm font-medium bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap"
        >
          全部
        </Link>
        {categories.map((cat) => (
          <Link
            key={cat.slug}
            to={`/category/${cat.slug}`}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            <i className={`${cat.icon} text-sm`}></i>
            {cat.name}
          </Link>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ title, subtitle, linkTo }: { title: string; subtitle: string; linkTo?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{title}</h2>
        <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>
      </div>
      {linkTo && (
        <Link to={linkTo} className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
          {t('home.viewAll')} <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

function HotSection() {
  const { t } = useTranslation();
  const hotComics = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 6);

  return (
    <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
      <SectionHeader
        title={t('home.hotTitle')}
        subtitle={t('home.hotSubtitle')}
        linkTo="/category/all"
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {hotComics.map((comic) => (
          <ComicCard key={comic.id} comic={comic} />
        ))}
      </div>
    </section>
  );
}

function LatestSection() {
  const { t } = useTranslation();
  const latestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 6);

  return (
    <section className="bg-background-100 py-14">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <SectionHeader
          title={t('home.latestTitle')}
          subtitle={t('home.latestSubtitle')}
          linkTo="/category/all"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {latestComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RankingSection() {
  const { t } = useTranslation();
  const topComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 10);

  return (
    <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
      <SectionHeader
        title={t('home.rankingTitle')}
        subtitle={t('home.rankingSubtitle')}
      />
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        {topComics.map((comic, index) => (
          <Link
            key={comic.id}
            to={`/comic/${comic.slug}`}
            className={`flex items-center gap-4 p-4 hover:bg-background-100 transition-colors ${
              index < topComics.length - 1 ? 'border-b border-background-200/70' : ''
            }`}
          >
            <span
              className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${
                index < 3
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-200 text-foreground-600'
              }`}
            >
              {index + 1}
            </span>
            <img
              src={comic.cover}
              alt={comic.title}
              className="w-12 h-16 rounded object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1">{comic.title}</h3>
              <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="flex items-center gap-1 text-sm text-accent-500">
                <i className="ri-star-fill text-xs"></i>
                {comic.rating}
              </span>
              <span className="text-xs text-foreground-500">
                {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}{t('home.views')}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t } = useTranslation();

  const siteSeo = {
    title: '韩漫漫画大全列表在线观看_好看的韩漫漫画推荐_免费韩漫漫画全集_韩漫网',
    description: '韩漫网支持韩漫漫画大全列表在线观看，精选好看的韩漫漫画推荐和免费韩漫漫画全集，更新速度快内容新颖，满足您追更需求。',
    keywords: '韩漫漫画大全列表在线观看,好看的韩漫漫画推荐,免费韩漫漫画全集,韩漫网',
    canonicalUrl: 'https://hanmanwang.com/',
    ogTitle: '韩漫漫画大全列表在线观看_好看的韩漫漫画推荐_免费韩漫漫画全集_韩漫网',
    ogDescription: '韩漫网支持韩漫漫画大全列表在线观看，精选好看的韩漫漫画推荐和免费韩漫漫画全集，更新速度快内容新颖，满足您追更需求。',
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "韩漫网",
        "url": "https://hanmanwang.com/",
        "description": "韩漫网支持韩漫漫画大全列表在线观看，精选好看的韩漫漫画推荐和免费韩漫漫画全集，更新速度快内容新颖，满足您追更需求。",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://hanmanwang.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "韩漫网",
        "url": "https://hanmanwang.com/",
        "description": "韩漫网是致力于为漫画爱好者提供最新、最全、最优质的韩漫资源平台。",
        "email": "contact@hanmanwang.com"
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "韩漫网首页",
        "url": "https://hanmanwang.com/",
        "description": "韩漫漫画大全列表在线观看，精选好看的韩漫漫画推荐和免费韩漫漫画全集。",
        "isPartOf": { "@id": "https://hanmanwang.com/#website" }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://hanmanwang.com/"
          }
        ]
      }
    ]
  };

  return (
    <>
      <PageSeo {...siteSeo} />
      <HeroSection />
      <CategoryPills />
      <HotSection />
      <LatestSection />
      <RankingSection />
    </>
  );
}